import { IValidationReport, IValidationResult } from '../types';

/**
 * 原生 ECharts options 校验器
 * 提供 4 层检测：
 * L1: JSON 语法
 * L2: 数据类型（必须是对象）
 * L3: 结构（必须包含 series；常用图表需包含 xAxis/yAxis；饼图等可不要求）
 * L4: 内容（series 元素结构、function 字符串风险提示等）
 *
 * 设计原则：
 *  - 仅做轻量校验，避免过度限制 echarts 自身的灵活配置
 *  - 校验通过后直接将原始 options 传递给 AgentEchart 渲染
 */
export class EchartsOptionValidator {
  private errors: IValidationResult[] = [];
  private warnings: IValidationResult[] = [];
  private successes: IValidationResult[] = [];

  public validate(input: string): IValidationReport {
    this.reset();

    // L1: JSON 语法
    const parsed = this.validateJson(input);
    if (!parsed.success) {
      return this.generateReport(false);
    }

    const {data} = parsed;

    // L2: 数据类型
    if (!this.validateDataType(data)) {
      return this.generateReport(false);
    }

    // L3: 结构（series 必填）
    if (!this.validateStructure(data)) {
      return this.generateReport(false);
    }

    // L4: 内容
    this.validateContent(data);

    const canRender = this.errors.length === 0;
    return this.generateReport(canRender, data);
  }

  // ---------- L1 ----------
  private validateJson(input: string): { success: boolean; data?: any; } {
    if (!input?.trim()) {
      this.addError('输入内容为空', 'input');
      return { success: false };
    }

    try {
      const data = JSON.parse(input);
      this.addSuccess('✓ JSON 格式验证通过');
      return { success: true, data };
    } catch (error: any) {
      const match = error.message?.match?.(/position (\d+)/);
      const position = match ? parseInt(match[1], 10) : undefined;
      const lineColumn = position !== undefined ? this.getLineColumn(input, position) : undefined;
      this.addError(`JSON 解析失败: ${error.message}`, 'json', error.stack, lineColumn);
      return { success: false };
    }
  }

  // ---------- L2 ----------
  private validateDataType(data: any): boolean {
    if (data === null || typeof data !== 'object' || Array.isArray(data)) {
      this.addError('数据格式错误：原生 ECharts options 应为一个对象 { ... }', 'root');
      return false;
    }
    this.addSuccess('✓ 数据类型验证通过');
    return true;
  }

  // ---------- L3 ----------
  private validateStructure(data: any): boolean {
    let hasError = false;

    // series 必填
    if (!('series' in data)) {
      this.addError('缺少必要字段: series', 'series');
      hasError = true;
    } else if (data.series == null) {
      this.addError('series 不能为空', 'series');
      hasError = true;
    } else if (!Array.isArray(data.series) && typeof data.series !== 'object') {
      this.addError('series 必须为数组或对象', 'series');
      hasError = true;
    }

    if (!hasError) {
      this.addSuccess('✓ 数据结构验证通过');
    }
    return !hasError;
  }

  // ---------- L4 ----------
  private validateContent(data: any): void {
    const seriesArr = Array.isArray(data.series) ? data.series : [data.series];

    if (seriesArr.length === 0) {
      this.addWarning('series 为空数组，图表将无内容渲染', 'series');
    }

    // 判断是否为非笛卡尔坐标系图表（饼/雷达/仪表盘等无需 xAxis/yAxis）
    const NON_CARTESIAN_TYPES = new Set([
      'pie', 'radar', 'gauge', 'funnel', 'sunburst', 'tree', 'treemap', 'sankey', 'graph', 'wordCloud',
    ]);
    const allNonCartesian = seriesArr.length > 0 && seriesArr.every((s: any) => s && NON_CARTESIAN_TYPES.has(s.type));

    if (!allNonCartesian) {
      if (!('xAxis' in data)) {
        this.addWarning('缺少 xAxis 配置，部分图表类型可能渲染异常', 'xAxis');
      }
      if (!('yAxis' in data)) {
        this.addWarning('缺少 yAxis 配置，部分图表类型可能渲染异常', 'yAxis');
      }
    }

    // 检查每个 series 是否含 type 与 data
    seriesArr.forEach((s: any, idx: number) => {
      const prefix = `series[${idx}]`;
      if (!s || typeof s !== 'object') {
        this.addError(`${prefix} 必须为对象`, prefix);
        return;
      }
      if (!s.type) {
        this.addWarning(`${prefix} 缺少 type 字段，可能无法正确识别图表类型`, `${prefix}.type`);
      }
      if (!('data' in s) && !('encode' in s) && !('datasetIndex' in s)) {
        this.addWarning(
          `${prefix} 未提供 data / encode / datasetIndex，图表可能无内容`,
          `${prefix}.data`,
        );
      }
    });

    // function 字符串风险提示（JSON 不支持函数，提醒用户使用对象配置）
    const stringified = JSON.stringify(data);
    if (/"function\s*\(/.test(stringified)) {
      this.addWarning(
        '检测到字段值为 function 字符串，JSON 中无法承载真实函数，formatter 等回调将以字符串形式被忽略',
        'function',
      );
    }

    if (this.errors.length === 0) {
      this.addSuccess('✓ 数据内容验证通过');
    }
  }

  // ---------- helpers ----------
  private getLineColumn(input: string, position: number): { line: number; column: number; } {
    const lines = input.substring(0, position).split('\n');
    return { line: lines.length, column: lines[lines.length - 1].length + 1 };
  }

  private addError(
    message: string,
    field?: string,
    details?: string,
    position?: { line?: number; column?: number; },
  ): void {
    this.errors.push({ level: 'error', message, field, details, position });
  }

  private addWarning(message: string, field?: string, details?: string): void {
    this.warnings.push({ level: 'warning', message, field, details });
  }

  private addSuccess(message: string): void {
    this.successes.push({ level: 'success', message });
  }

  private reset(): void {
    this.errors = [];
    this.warnings = [];
    this.successes = [];
  }

  private generateReport(canRender: boolean, inputData?: any): IValidationReport {
    return {
      passed: this.errors.length === 0,
      canRender,
      errors: [...this.errors],
      warnings: [...this.warnings],
      successes: [...this.successes],
      timestamp: Date.now(),
      inputData,
    };
  }
}
